import { ThemeProvider } from "@/contexts/ThemeContext";
import { Box } from "@mantine/core";
import { AuthProvider } from "@/contexts/auth/AuthProvider";
import { SiteProvider } from "@/contexts/site";
import { SentryErrorBoundary, trackPageView } from "@/lib/sentry";
import { lazyWithReload } from "@/lib/lazyWithReload";
import { Routes, Route, Navigate, useLocation, useNavigationType } from "react-router-dom";
import { useEffect, Suspense } from "react";
import { useVersionCheck } from '@/hooks/useVersionCheck';
import { useAuth } from '@/contexts/auth';
import { useNavigate } from 'react-router-dom';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import usePerformanceMonitor from '@/hooks/usePerformanceMonitor';
import { useScrollToTop } from '@/hooks/useScrollToTop';
import { MinimalAppSkeleton } from '@/components/ui/app-skeleton';
import { GlobalLoadingIndicator } from '@/components/ui/global-loading-indicator';
import { useBackendFrontendVersion } from '@/hooks/useBackendFrontendVersion';
import { setFrontendCompatibilityStatus } from '@/lib/api/core';

// Lazy load page components for better performance
const Login = lazyWithReload(() => import('@/pages/Login'));
const LauncherDevicesPage = lazyWithReload(() => import('@/pages/launcher/LauncherDevicesPage'));
const LauncherDeviceDetailPage = lazyWithReload(() => import('@/pages/launcher/LauncherDeviceDetailPage'));
const OAuthCallback = lazyWithReload(() => import('@/pages/OAuthCallback'));
const AuthorizePage = lazyWithReload(() => import('@/pages/oauth/AuthorizePage'));

const Dashboard = lazyWithReload(() => import('@/pages/Dashboard'));
const CustomerSelect = lazyWithReload(() => import('@/pages/CustomerSelect'));
const SiteCreatePage = lazyWithReload(() => import('@/pages/sites/SiteCreatePage'));
const SiteEditPage = lazyWithReload(() => import('@/pages/sites/SiteEditPage'));
const SiteDetailPage = lazyWithReload(() => import('@/pages/sites/SiteDetailPage'));
const AddressPage = lazyWithReload(() => import('@/pages/addresses/AddressPage'));
const AddressDetailPage = lazyWithReload(() => import('@/pages/addresses/AddressDetailPage'));
const FloorsPage = lazyWithReload(() => import('@/pages/floors/FloorsPage'));
const FloorDetailPage = lazyWithReload(() => import('@/pages/floors/FloorDetailPage'));
const RoomsPage = lazyWithReload(() => import('@/pages/rooms/RoomsPage'));
const RoomDetailPage = lazyWithReload(() => import('@/pages/rooms/RoomDetailPage'));
const CreateRoomPage = lazyWithReload(() => import('@/pages/rooms/CreateRoomPage'));
const EditRoomPage = lazyWithReload(() => import('@/pages/rooms/EditRoomPage'));
const DoorsPage = lazyWithReload(() => import('@/pages/doors/DoorsPage'));
const FloorConnectionsPage = lazyWithReload(() => import('@/pages/floor-connections/FloorConnectionsPage'));
const DoorDetailPage = lazyWithReload(() => import('@/pages/doors/DoorDetailPage'));
const TenantsPage = lazyWithReload(() => import('@/pages/tenants/TenantsPage'));
const TenantDetailPage = lazyWithReload(() => import('@/pages/tenants/TenantDetailPage'));
const TenantCreatePage = lazyWithReload(() => import('@/pages/tenants/TenantCreatePage'));
const TenantEditPage = lazyWithReload(() => import('@/pages/tenants/TenantEditPage'));
const TenantCapacityPage = lazyWithReload(() => import('@/pages/tenants/TenantCapacityPage'));
const ContractsPage = lazyWithReload(() => import('@/pages/contracts/ContractsPage'));
const ContractDetailPage = lazyWithReload(() => import('@/pages/contracts/ContractDetailPage'));
const ContractCreatePage = lazyWithReload(() => import('@/pages/contracts/ContractCreatePage'));
const UsersPage = lazyWithReload(() => import('@/pages/users/UsersPage'));
const UserDetailPage = lazyWithReload(() => import('@/pages/users/UserDetailPage'));
const IdentityHubPage = lazyWithReload(() => import('@/pages/identity/IdentityHubPage'));
const ColleagueInvitesPage = lazyWithReload(() => import('@/pages/colleague-invites/ColleagueInvitesPage'));
const AxemaDashboard = lazyWithReload(() => import('@/pages/axema/AxemaDashboard'));
const ScreenTinkerManagePage = lazyWithReload(() => import('@/pages/signage/ScreenTinkerManagePage'));
const ScreenTinkerDetailPage = lazyWithReload(() => import('@/pages/signage/ScreenTinkerDetailPage'));
const SignageBoardsPage = lazyWithReload(() => import('@/pages/signage/SignageBoardsPage'));
const SignageBoardCreatePage = lazyWithReload(() => import('@/pages/signage/SignageBoardCreatePage'));
const SignageBoardEditPage = lazyWithReload(() => import('@/pages/signage/SignageBoardEditPage'));
const AxemaManagePage = lazyWithReload(() => import('@/pages/axema/AxemaManagePage'));
const AxemaSystemDetailPage = lazyWithReload(() => import('@/pages/axema/AxemaSystemDetailPage'));
const AxemaDoorDetailPage = lazyWithReload(() => import('@/pages/axema/AxemaDoorDetailPage'));
const AxemaAccessGroupDetailPage = lazyWithReload(() => import('@/pages/axema/AxemaAccessGroupDetailPage'));
const AxemaScheduleEditorPage = lazyWithReload(() => import('@/pages/axema/AxemaScheduleEditorPage'));
const CustomersPage = lazyWithReload(() => import('@/pages/customers/CustomersPage'));
const TimelinePage = lazyWithReload(() => import('@/pages/timeline/TimelinePage'));
const AuditLogPage = lazyWithReload(() => import('@/pages/audit/AuditLogPage'));
const ChangelogPage = lazyWithReload(() => import('@/pages/ChangelogPage'));
// Staff tooling (#459): operational settings + the product-video demo world
const SystemSettingsPage = lazyWithReload(() => import('@/pages/staff/SystemSettingsPage'));
const DemoDataPage = lazyWithReload(() => import('@/pages/staff/DemoDataPage'));
const NotFound = lazyWithReload(() => import('@/pages/NotFound'));
const NoAccess = lazyWithReload(() => import('@/pages/NoAccess'));

// Network pages
const NetworksPage = lazyWithReload(() => import('@/pages/networks/NetworksPage'));
const NetworkDetailPage = lazyWithReload(() => import('@/pages/networks/NetworkDetailPage'));
const NetworkCreatePage = lazyWithReload(() => import('@/pages/networks/NetworkCreatePage'));
const NetworkEditPage = lazyWithReload(() => import('@/pages/networks/NetworkEditPage'));
const SpeedLimitsPage = lazyWithReload(() => import('@/pages/networks/SpeedLimitsPage'));
const SpeedLimitDetailPage = lazyWithReload(() => import('@/pages/networks/SpeedLimitDetailPage'));
const SpeedLimitCreatePage = lazyWithReload(() => import('@/pages/networks/SpeedLimitCreatePage'));
const SpeedLimitEditPage = lazyWithReload(() => import('@/pages/networks/SpeedLimitEditPage'));
const CapacityRankingPage = lazyWithReload(() => import('@/pages/networks/CapacityRankingPage'));
const CapacityNetworkPage = lazyWithReload(() => import('@/pages/networks/CapacityNetworkPage'));
const ExternalIPsPage = lazyWithReload(() => import('@/pages/external-ips/ExternalIPsPage'));
const ExternalIPDetailPage = lazyWithReload(() => import('@/pages/external-ips/ExternalIPDetailPage'));
const ExternalIPCreatePage = lazyWithReload(() => import('@/pages/external-ips/ExternalIPCreatePage'));

// Calendar feeds (#73)
const CalendarFeedsPage = lazyWithReload(() => import('@/pages/calendar-feeds/CalendarFeedsPage'));

// Audio library (#94 phase 5)
const AudioLibraryPage = lazyWithReload(() => import('@/pages/audio-library/AudioLibraryPage'));
const ImageGroupEditPage = lazyWithReload(() => import('@/pages/audio-library/ImageGroupEditPage'));

// Announcements (#94 phase 5)
const AnnouncementsPage = lazyWithReload(() => import('@/pages/announcements/AnnouncementsPage'));
const AddAnnouncementPage = lazyWithReload(() => import('@/pages/announcements/AddAnnouncementPage'));
const EditAnnouncementPage = lazyWithReload(() => import('@/pages/announcements/EditAnnouncementPage'));

// Wireless pages
const WirelessPage = lazyWithReload(() => import('@/pages/wireless/WirelessPage'));
const WirelessDetailPage = lazyWithReload(() => import('@/pages/wireless/WirelessDetailPage'));
const WirelessCreatePage = lazyWithReload(() => import('@/pages/wireless/WirelessCreatePage'));
const WirelessEditPage = lazyWithReload(() => import('@/pages/wireless/WirelessEditPage'));
const WirelessCredentialsPage = lazyWithReload(() => import('@/pages/wireless/WirelessCredentialsPage'));

// Outlet pages
const OutletsPage = lazyWithReload(() => import('@/pages/outlets/OutletsPage'));
const OutletDetailPage = lazyWithReload(() => import('@/pages/outlets/OutletDetailPage'));

// CRM Devices page (crm.Device infrastructure — switches/routers/APs)
const DevicesPage = lazyWithReload(() => import('@/pages/devices/DevicesPage'));
const DeviceDetailPage = lazyWithReload(() => import('@/pages/devices/DeviceDetailPage'));

// Equipment pages
const EquipmentPage = lazyWithReload(() => import('@/pages/equipment/EquipmentPage'));
const EquipmentDetailPage = lazyWithReload(() => import('@/pages/equipment/EquipmentDetailPage'));
const EquipmentEditPage = lazyWithReload(() => import('@/pages/equipment/EquipmentEditPage'));
const EquipmentCreatePage = lazyWithReload(() => import('@/pages/equipment/EquipmentCreatePage'));

// Furniture pages
const FurniturePage = lazyWithReload(() => import('@/pages/furniture/FurniturePage'));
const FurnitureDetailPage = lazyWithReload(() => import('@/pages/furniture/FurnitureDetailPage'));
const FurnitureEditPage = lazyWithReload(() => import('@/pages/furniture/FurnitureEditPage'));
const FurnitureCreatePage = lazyWithReload(() => import('@/pages/furniture/FurnitureCreatePage'));

// Desk pages
const DesksPage = lazyWithReload(() => import('@/pages/desks/DesksPage'));
const DeskDetailPage = lazyWithReload(() => import('@/pages/desks/DeskDetailPage'));
const DeskEditPage = lazyWithReload(() => import('@/pages/desks/DeskEditPage'));
const DeskCreatePage = lazyWithReload(() => import('@/pages/desks/DeskCreatePage'));
const DeskTypesPage = lazyWithReload(() => import('@/pages/desks/DeskTypesPage'));

// Doorbell pages
const DoorbellsPage = lazyWithReload(() => import('@/pages/doorbell/DoorbellsPage'));
const DoorbellDetailPage = lazyWithReload(() => import('@/pages/doorbell/DoorbellDetailPage'));
const PresetMessagesPage = lazyWithReload(() => import('@/pages/doorbell/PresetMessagesPage'));
const DoorbellUpsertPage = lazyWithReload(() => import('@/pages/doorbell/DoorbellUpsertPage'));

// FAQ pages
const FAQDashboard = lazyWithReload(() => import('@/pages/faq/FAQDashboard'));
const FAQCategoryDetailPage = lazyWithReload(() => import('@/pages/faq/FAQCategoryDetailPage'));
const FAQArticleEditPage = lazyWithReload(() => import('@/pages/faq/FAQArticleEditPage'));
const FAQArticleViewPage = lazyWithReload(() => import('@/pages/faq/FAQArticleViewPage'));
const GuidesPage = lazyWithReload(() => import('@/pages/faq/GuidesPage'));
const GuideEditPage = lazyWithReload(() => import('@/pages/faq/GuideEditPage'));

// Booking dashboard (#18 — meeting-room + no-show stats)
const MeetingRoomDashboardPage = lazyWithReload(() => import('@/pages/booking/MeetingRoomDashboardPage'));

// Papercut pages
const PapercutDashboard = lazyWithReload(() => import('@/pages/papercut/PapercutDashboard'));
const ManageSystemsPage = lazyWithReload(() => import('@/pages/papercut/ManageSystemsPage'));

// Mailroom
const MailroomPage = lazyWithReload(() => import('@/pages/mailroom/MailroomPage'));
// IoT
const IotDevicesPage = lazyWithReload(() => import('@/pages/iot/IotDevicesPage'));
const IotDeviceDetailPage = lazyWithReload(() => import('@/pages/iot/IotDeviceDetailPage'));
const IotMappingPage = lazyWithReload(() => import('@/pages/iot/IotMappingPage'));
const IotConnectionsPage = lazyWithReload(() => import('@/pages/iot/IotConnectionsPage'));
const IotProviderPage = lazyWithReload(() => import('@/pages/iot/IotProviderPage'));
const IotDevicesTabRedirect = lazyWithReload(() => import('@/pages/iot/IotDevicesTabRedirect'));
const AcreDashboard = lazyWithReload(() => import('@/pages/acre/AcreDashboard'));
const AcreConnectionDetailPage = lazyWithReload(() => import('@/pages/acre/AcreConnectionDetailPage'));
const WisenetDashboard = lazyWithReload(() => import('@/pages/wisenet/WisenetDashboard'));
const WisenetManageSystemsPage = lazyWithReload(() => import('@/pages/wisenet/ManageSystemsPage'));
const WisenetSystemDetailPage = lazyWithReload(() => import('@/pages/wisenet/WisenetSystemDetailPage'));
const ArubaDashboard = lazyWithReload(() => import('@/pages/arubainstanton/ArubaDashboard'));
const ArubaManagePage = lazyWithReload(() => import('@/pages/arubainstanton/ArubaManagePage'));
const ArubaSystemDetailPage = lazyWithReload(() => import('@/pages/arubainstanton/ArubaSystemDetailPage'));

// UniFi
const UnifiDashboard = lazyWithReload(() => import('@/pages/unifi/UnifiDashboard'));
const UnifiManagePage = lazyWithReload(() => import('@/pages/unifi/UnifiManagePage'));
const UnifiRFDashboard = lazyWithReload(() => import('@/pages/unifi-rf/UnifiRFDashboard'));
const RFScanDetailPage = lazyWithReload(() => import('@/pages/unifi-rf/RFScanDetailPage'));

// Mikrotik pages - lazy loaded
const MikrotikDashboardPage = lazyWithReload(() => import('@/pages/mikrotik/MikrotikDashboardPage'));
const MikrotikDevicesPage = lazyWithReload(() => import('@/pages/mikrotik/MikrotikDevicesPage'));
const MikrotikDeviceDetailPage = lazyWithReload(() => import('@/pages/mikrotik/MikrotikDeviceDetailPage'));
const MikrotikDeviceCreatePage = lazyWithReload(() => import('@/pages/mikrotik/MikrotikDeviceCreatePage'));
const MikrotikDeviceEditPage = lazyWithReload(() => import('@/pages/mikrotik/MikrotikDeviceEditPage'));
const MikrotikDeviceConfigPage = lazyWithReload(() => import('@/pages/mikrotik/MikrotikDeviceConfigPage'));
const MikrotikInterfaceDetailPage = lazyWithReload(() => import('@/pages/mikrotik/MikrotikInterfaceDetailPage'));

// Arista pages - lazy loaded
const AristaDevicesPage = lazyWithReload(() => import('@/pages/arista/AristaDevicesPage'));
const AristaDeviceDetailPage = lazyWithReload(() => import('@/pages/arista/AristaDeviceDetailPage'));
const AristaDeviceCreatePage = lazyWithReload(() => import('@/pages/arista/AristaDeviceCreatePage'));
const AristaDeviceEditPage = lazyWithReload(() => import('@/pages/arista/AristaDeviceEditPage'));
const AristaDeviceConfigPage = lazyWithReload(() => import('@/pages/arista/AristaDeviceConfigPage'));
const AristaInterfaceDetailPage = lazyWithReload(() => import('@/pages/arista/AristaInterfaceDetailPage'));
const AristaDashboardPage = lazyWithReload(() => import('@/pages/arista/AristaDashboardPage'));

// Support page - lazy loaded (tabbed: Inbox + My Subscriptions)
const SupportPage = lazyWithReload(() => import('@/pages/support/SupportPage'));
const SupportTicketDetailPage = lazyWithReload(() => import('@/pages/support/SupportTicketDetailPage'));

// Flow manager - operator-defined lifecycle workflows (staff + customer admins)
const FlowsPage = lazyWithReload(() => import('@/pages/flows/FlowsPage'));
const FlowDetailPage = lazyWithReload(() => import('@/pages/flows/FlowDetailPage'));
// Billing (operator invoicing) — customer admin only, GA'd (no feature flag)
const BillingProfilePage = lazyWithReload(() => import('@/pages/billing/BillingProfilePage'));
const BillingReadinessPage = lazyWithReload(() => import('@/pages/billing/BillingReadinessPage'));
const BillingConnectionsPage = lazyWithReload(() => import('@/pages/billing/BillingConnectionsPage'));
const BillingRunsPage = lazyWithReload(() => import('@/pages/billing/BillingRunsPage'));
const BillingRunDetailPage = lazyWithReload(() => import('@/pages/billing/BillingRunDetailPage'));
const BillingInvoicesPage = lazyWithReload(() => import('@/pages/billing/BillingInvoicesPage'));
const BillingInvoiceDetailPage = lazyWithReload(() => import('@/pages/billing/BillingInvoiceDetailPage'));
const PriceAdjustmentRedirectPage = lazyWithReload(() => import('@/pages/billing/PriceAdjustmentRedirectPage'));
const BillingArticlesPage = lazyWithReload(() => import('@/pages/billing/BillingArticlesPage'));
const ExtraChargeViewPage = lazyWithReload(() => import('@/pages/billing/ExtraChargeViewPage'));

// Needs Attention - full list behind the dashboard preview card
const NeedsAttentionPage = lazyWithReload(() => import('@/pages/NeedsAttentionPage'));

// Sidecar pages - lazy loaded
const SidecarPage = lazyWithReload(() => import('@/pages/sidecar/SidecarPage'));

// Slack pages - lazy loaded
const SlackBotsPage = lazyWithReload(() => import('@/pages/slack/SlackBotsPage'));

// Certificate pages - lazy loaded
const CertificatesPage = lazyWithReload(() => import('@/pages/certificates/CertificatesPage'));
const CertificateDetailPage = lazyWithReload(() => import('@/pages/certificates/CertificateDetailPage'));
const CertificateCreatePage = lazyWithReload(() => import('@/pages/certificates/CertificateCreatePage'));
const CertificateEditPage = lazyWithReload(() => import('@/pages/certificates/CertificateEditPage'));

// Locations overview page
const LocationsOverviewPage = lazyWithReload(() => import('@/pages/locations/LocationsOverviewPage'));

// Floorplan pages - lazy loaded
const FloorplansListPage = lazyWithReload(() => import('@/pages/floorplans/FloorplansListPage'));
const FloorplanDetailPage = lazyWithReload(() => import('@/pages/floorplans/FloorplanDetailPage'));
const FloorplanEditorPage = lazyWithReload(() => import('@/pages/floorplans/FloorplanEditorPage'));
const FloorplanPointsPage = lazyWithReload(() => import('@/pages/floorplans/FloorplanPointsPage'));
const FloorplanNavigationPage = lazyWithReload(() => import('@/pages/floorplans/FloorplanNavigationPage'));
const FloorplanInteriorPage = lazyWithReload(() => import('@/pages/floorplans/FloorplanInteriorPage'));
const FloorplanLayersPage = lazyWithReload(() => import('@/pages/floorplans/FloorplanLayersPage'));

// Client pages - lazy loaded
const ClientDetailPage = lazyWithReload(() => import('@/pages/clients/ClientDetailPage'));
const ClientsListPage = lazyWithReload(() => import('@/pages/clients/ClientsListPage'));
// Network diagnostics - Diagnostic Sessions (replaces Connectivity tests + Speed test)
const DiagnosticSessionsPage = lazyWithReload(() => import('@/pages/network/DiagnosticSessionsPage'));
const DiagnosticSessionDetailPage = lazyWithReload(() => import('@/pages/network/DiagnosticSessionDetailPage'));

// Consolidated sync status across networks/wireless/outlets
const SyncStatusDashboard = lazyWithReload(() => import('@/pages/sync-status/SyncStatusDashboard'));

// Auth components
import { AuthGuard } from '@/components/auth/AuthGuard';
import { CustomerRedirect } from '@/components/auth/CustomerRedirect';
import { CustomerAdminGuard } from '@/components/auth/CustomerAdminGuard';
import { SuperAdminGuard } from '@/components/auth/SuperAdminGuard';
import { StaffGuard } from '@/components/auth/StaffGuard';
import { MaintenanceGate } from '@/components/system/MaintenanceScreen';
import { LegacyCapacityRedirect } from '@/components/networks/LegacyCapacityRedirect';

// Track route changes for better session replays
const RouteChangeTracker = () => {
  const location = useLocation();
  const navigationType = useNavigationType();

  // Scroll to top on route change
  useScrollToTop();

  useEffect(() => {
    const pageName = location.pathname.split('/').filter(Boolean).join('/') || 'root';
    const pageTitle = document.title || pageName;
    trackPageView(location.pathname, pageTitle);
  }, [location, navigationType]);

  return null;
};

function useCrossTabLoginSync() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const onStorage = (event) => {
      if (event.key === 'login-event') {
        if (!isAuthenticated) {
          window.location.reload();
        }
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [isAuthenticated, navigate]);
}

// Wrap the app with necessary providers
const App = () => {
  useVersionCheck();
  useCrossTabLoginSync();
  usePerformanceMonitor();
  const { data: versionInfo, isError: versionError } = useBackendFrontendVersion();

  useEffect(() => {
    setFrontendCompatibilityStatus(versionInfo?.compatibility_status ?? 'unknown');
  }, [versionInfo?.compatibility_status]);

  return (
    <SentryErrorBoundary>
      <ThemeProvider>
          <AuthProvider>
            <SiteProvider>
                <GlobalLoadingIndicator />
                {versionInfo?.compatibility_status === 'incompatible' && (
                  <Box pos="fixed" top={0} left={0} right={0} style={{ zIndex: 60, backgroundColor: 'var(--mantine-color-red-6)', color: 'white', padding: '8px 12px', fontSize: 14, textAlign: 'center' }}>
                    Frontend {typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'dev'} is incompatible with backend {versionInfo?.backend_version ?? (versionError ? 'down' : '...')}.
                    Update frontend to at least {versionInfo?.minimum_frontend_version ?? 'required version'} to enable changes.
                  </Box>
                )}
                <RouteChangeTracker />
                <MaintenanceGate>
                <Suspense fallback={<MinimalAppSkeleton />}>
                  <Routes>
                    {/* Public routes */}
                    <Route path="/login" element={<Login />} />
                    <Route path="/oauth-callback" element={<OAuthCallback />} />
                    <Route path="/no-access" element={<NoAccess />} />
                    <Route path="/oauth/authorize" element={<AuthorizePage />} />

                    {/* Customer selection route */}
                    <Route path="/select-customer" element={<CustomerRedirect />} />

                    {/* Protected routes */}
                    <Route element={<AuthGuard />}>
                      <Route path="/dashboard" element={<Dashboard />} />
                      <Route path="/addresses" element={<CustomerAdminGuard><AddressPage /></CustomerAdminGuard>} />

                      {/* Site routes */}
                      <Route path="/site" element={<CustomerAdminGuard><SiteDetailPage /></CustomerAdminGuard>} />
                      <Route path="/site/new" element={<CustomerAdminGuard><SiteCreatePage /></CustomerAdminGuard>} />
                      <Route path="/site/edit" element={<CustomerAdminGuard><SiteEditPage /></CustomerAdminGuard>} />

                      {/* CRM entity routes */}
                      <Route path="/addresses/view/:addressId" element={<CustomerAdminGuard><AddressDetailPage /></CustomerAdminGuard>} />
                      <Route path="/floors" element={<CustomerAdminGuard><FloorsPage /></CustomerAdminGuard>} />
                      <Route path="/floors/view/:floorId" element={<CustomerAdminGuard><FloorDetailPage /></CustomerAdminGuard>} />
                      <Route path="/rooms" element={<CustomerAdminGuard><RoomsPage /></CustomerAdminGuard>} />
                      <Route path="/rooms/new" element={<CustomerAdminGuard><CreateRoomPage /></CustomerAdminGuard>} />
                      <Route path="/rooms/view/:roomId" element={<CustomerAdminGuard><RoomDetailPage /></CustomerAdminGuard>} />
                      <Route path="/rooms/edit/:roomId" element={<CustomerAdminGuard><EditRoomPage /></CustomerAdminGuard>} />
                      <Route path="/doors" element={<CustomerAdminGuard><DoorsPage /></CustomerAdminGuard>} />
                      <Route path="/floor-connections" element={<CustomerAdminGuard><FloorConnectionsPage /></CustomerAdminGuard>} />
                      <Route path="/doors/view/:doorId" element={<CustomerAdminGuard><DoorDetailPage /></CustomerAdminGuard>} />

                      <Route path="/tenants" element={<CustomerAdminGuard><TenantsPage /></CustomerAdminGuard>} />
                      <Route path="/tenants/view/:tenantId" element={<CustomerAdminGuard><TenantDetailPage /></CustomerAdminGuard>} />
                      {/* A tenant's capacity IS its network's capacity (Tenant.network is a
                          single FK) — the same view as /capacity/:networkId, resolved through
                          the tenant. Admin-gated because it sits under /tenants, which is
                          customerAdminOnly; capacity itself stays open at /capacity. */}
                      <Route path="/tenants/view/:tenantId/capacity" element={<CustomerAdminGuard><TenantCapacityPage /></CustomerAdminGuard>} />
                      <Route path="/tenants/new" element={<CustomerAdminGuard><TenantCreatePage /></CustomerAdminGuard>} />
                      <Route path="/tenants/edit/:tenantId" element={<CustomerAdminGuard><TenantEditPage /></CustomerAdminGuard>} />
                      <Route path="/contracts" element={<CustomerAdminGuard><ContractsPage /></CustomerAdminGuard>} />
                      <Route path="/contracts/new" element={<CustomerAdminGuard><ContractCreatePage /></CustomerAdminGuard>} />
                      <Route path="/contracts/view/:contractId" element={<CustomerAdminGuard><ContractDetailPage /></CustomerAdminGuard>} />
                      <Route path="/users" element={<CustomerAdminGuard><UsersPage /></CustomerAdminGuard>} />
                      <Route path="/users/view/:userId" element={<CustomerAdminGuard><UserDetailPage /></CustomerAdminGuard>} />
                      <Route path="/colleague-invites/:tab?" element={<CustomerAdminGuard><ColleagueInvitesPage /></CustomerAdminGuard>} />
                      <Route path="/identity/:tab?" element={<IdentityHubPage />} />
                      <Route path="/launcher/devices" element={<CustomerAdminGuard><LauncherDevicesPage /></CustomerAdminGuard>} />
                      <Route path="/launcher/devices/view/:id" element={<CustomerAdminGuard><LauncherDeviceDetailPage /></CustomerAdminGuard>} />

                      {/* Axema routes — specific paths before :tab? catch-all */}
                      <Route path="/axema/schedules/new" element={<CustomerAdminGuard><AxemaScheduleEditorPage /></CustomerAdminGuard>} />
                      <Route path="/axema/schedules/edit/:scheduleId" element={<CustomerAdminGuard><AxemaScheduleEditorPage /></CustomerAdminGuard>} />
                      <Route path="/axema/doors/view/:doorId" element={<CustomerAdminGuard><AxemaDoorDetailPage /></CustomerAdminGuard>} />
                      <Route path="/axema/access-groups/view/:accessGroupId" element={<CustomerAdminGuard><AxemaAccessGroupDetailPage /></CustomerAdminGuard>} />
                      <Route path="/axema/systems/view/:systemId" element={<CustomerAdminGuard><AxemaSystemDetailPage /></CustomerAdminGuard>} />
                      <Route path="/axema/manage" element={<CustomerAdminGuard><AxemaManagePage /></CustomerAdminGuard>} />
                      <Route path="/signage" element={<CustomerAdminGuard><SignageBoardsPage /></CustomerAdminGuard>} />
                      <Route path="/signage/new" element={<CustomerAdminGuard><SignageBoardCreatePage /></CustomerAdminGuard>} />
                      <Route path="/signage/edit/:id" element={<CustomerAdminGuard><SignageBoardEditPage /></CustomerAdminGuard>} />
                      <Route path="/signage/screentinker" element={<CustomerAdminGuard><ScreenTinkerManagePage /></CustomerAdminGuard>} />
                      <Route path="/signage/screentinker/view/:id" element={<CustomerAdminGuard><ScreenTinkerDetailPage /></CustomerAdminGuard>} />
                      <Route path="/axema/:tab?" element={<CustomerAdminGuard><AxemaDashboard /></CustomerAdminGuard>} />
                      <Route path="/customers" element={<CustomersPage />} />

                      {/* Network routes — specific paths before :tab? catch-all.
                          CustomerAdminGuard throughout: GET /crm/networks/overview/ and the
                          network/speed-limit CRUD behind these pages are all bare
                          @CustomerRequired(), which already 403s a non-admin customer user (#354). */}
                      <Route path="/networks/view/:networkId" element={<CustomerAdminGuard><NetworkDetailPage /></CustomerAdminGuard>} />
                      <Route path="/networks/new" element={<CustomerAdminGuard><NetworkCreatePage /></CustomerAdminGuard>} />
                      <Route path="/networks/edit/:networkId" element={<CustomerAdminGuard><NetworkEditPage /></CustomerAdminGuard>} />
                      <Route path="/networks/speed-limits" element={<CustomerAdminGuard><SpeedLimitsPage /></CustomerAdminGuard>} />
                      <Route path="/networks/speed-limits/view/:speedLimitId" element={<CustomerAdminGuard><SpeedLimitDetailPage /></CustomerAdminGuard>} />
                      <Route path="/networks/speed-limits/new" element={<CustomerAdminGuard><SpeedLimitCreatePage /></CustomerAdminGuard>} />
                      <Route path="/networks/speed-limits/edit/:speedLimitId" element={<CustomerAdminGuard><SpeedLimitEditPage /></CustomerAdminGuard>} />
                      <Route path="/networks/external-ips" element={<CustomerAdminGuard><ExternalIPsPage /></CustomerAdminGuard>} />
                      {/* Consolidated sync status across networks/wireless/outlets — static segments
                          outrank the dynamic :tab? so /networks/sync-status never resolves as a dashboard tab */}
                      <Route path="/networks/sync-status" element={<CustomerAdminGuard><SyncStatusDashboard /></CustomerAdminGuard>} />
                      <Route path="/networks/sync-status/:tab" element={<CustomerAdminGuard><SyncStatusDashboard /></CustomerAdminGuard>} />
                      {/* Legacy capacity deep links (bookmarks, chat messages) — capacity moved
                          out from under /networks/ in #354 so that /networks could finally be
                          gated. Deliberately unguarded: a redirect renders no page and makes no
                          request, and guarding it would bounce the very non-admin users capacity
                          is open to. Static "capacity" outranks the dynamic :tab? below it. */}
                      <Route path="/networks/capacity" element={<LegacyCapacityRedirect />} />
                      <Route path="/networks/capacity/:networkId" element={<LegacyCapacityRedirect />} />
                      <Route path="/networks/:tab?" element={<CustomerAdminGuard><NetworksPage /></CustomerAdminGuard>} />
                      {/* Connection capacity — vendor-neutral paths (the backend is registry-driven,
                          so these must not name a vendor). Open to any CRM user, not just a customer
                          admin: both endpoints are explicitly @CustomerRequired(admin_required=False)
                          (crm/api/capacity.py), so they live outside the /networks/ prefix rather than
                          inheriting its customer-admin guard. */}
                      <Route path="/capacity" element={<CapacityRankingPage />} />
                      <Route path="/capacity/:networkId" element={<CapacityNetworkPage />} />
                      <Route path="/external-ips" element={<CustomerAdminGuard><ExternalIPsPage /></CustomerAdminGuard>} />
                      <Route path="/external-ips/view/:externalIPId" element={<CustomerAdminGuard><ExternalIPDetailPage /></CustomerAdminGuard>} />
                      <Route path="/external-ips/new" element={<CustomerAdminGuard><ExternalIPCreatePage /></CustomerAdminGuard>} />

                      <Route path="/calendar-feeds" element={<CustomerAdminGuard><CalendarFeedsPage /></CustomerAdminGuard>} />
                      {/* Media Library (renamed from Audio Library — old
                          /audio-library URLs still resolve via a redirect
                          so bookmarks and admin link-outs don't break). */}
                      <Route path="/media-library/image-groups/new" element={<CustomerAdminGuard><ImageGroupEditPage /></CustomerAdminGuard>} />
                      <Route path="/media-library/image-groups/edit/:groupId" element={<CustomerAdminGuard><ImageGroupEditPage /></CustomerAdminGuard>} />
                      <Route path="/media-library/:tab?" element={<CustomerAdminGuard><AudioLibraryPage /></CustomerAdminGuard>} />
                      <Route path="/announcements" element={<CustomerAdminGuard><AnnouncementsPage /></CustomerAdminGuard>} />
                      <Route path="/announcements/new" element={<CustomerAdminGuard><AddAnnouncementPage /></CustomerAdminGuard>} />
                      <Route path="/announcements/edit/:announcementId" element={<CustomerAdminGuard><EditAnnouncementPage /></CustomerAdminGuard>} />

                      {/* Wireless routes — view uses /wireless/view/:id to avoid :tab? conflict */}
                      <Route path="/wireless/new" element={<CustomerAdminGuard><WirelessCreatePage /></CustomerAdminGuard>} />
                      <Route path="/wireless/edit/:wirelessId" element={<CustomerAdminGuard><WirelessEditPage /></CustomerAdminGuard>} />
                      <Route path="/wireless/view/:wirelessId/credentials" element={<CustomerAdminGuard><WirelessCredentialsPage /></CustomerAdminGuard>} />
                      <Route path="/wireless/view/:wirelessId" element={<CustomerAdminGuard><WirelessDetailPage /></CustomerAdminGuard>} />
                      <Route path="/wireless/:tab?" element={<CustomerAdminGuard><WirelessPage /></CustomerAdminGuard>} />

                      {/* Outlet routes */}
                      <Route path="/outlets" element={<CustomerAdminGuard><OutletsPage /></CustomerAdminGuard>} />
                      <Route path="/outlets/view/:outletId" element={<CustomerAdminGuard><OutletDetailPage /></CustomerAdminGuard>} />

                      {/* CRM Devices — site-scoped list, open like Networks/Outlets (not SuperAdminGuard-ed like the per-provider pages it links out to) */}
                      <Route path="/devices" element={<CustomerAdminGuard><DevicesPage /></CustomerAdminGuard>} />
                      <Route path="/devices/view/:id" element={<CustomerAdminGuard><DeviceDetailPage /></CustomerAdminGuard>} />

                      <Route path="/equipment" element={<CustomerAdminGuard><EquipmentPage /></CustomerAdminGuard>} />
                      <Route path="/equipment/new" element={<CustomerAdminGuard><EquipmentCreatePage /></CustomerAdminGuard>} />
                      <Route path="/equipment/view/:equipmentId" element={<CustomerAdminGuard><EquipmentDetailPage /></CustomerAdminGuard>} />
                      <Route path="/equipment/edit/:equipmentId" element={<CustomerAdminGuard><EquipmentEditPage /></CustomerAdminGuard>} />
                      <Route path="/furniture" element={<CustomerAdminGuard><FurniturePage /></CustomerAdminGuard>} />
                      <Route path="/furniture/new" element={<CustomerAdminGuard><FurnitureCreatePage /></CustomerAdminGuard>} />
                      <Route path="/furniture/view/:furnitureId" element={<CustomerAdminGuard><FurnitureDetailPage /></CustomerAdminGuard>} />
                      <Route path="/furniture/edit/:furnitureId" element={<CustomerAdminGuard><FurnitureEditPage /></CustomerAdminGuard>} />
                      <Route path="/desks" element={<CustomerAdminGuard><DesksPage /></CustomerAdminGuard>} />
                      <Route path="/desks/new" element={<CustomerAdminGuard><DeskCreatePage /></CustomerAdminGuard>} />
                      <Route path="/desks/types" element={<CustomerAdminGuard><DeskTypesPage /></CustomerAdminGuard>} />
                      <Route path="/desks/view/:deskId" element={<CustomerAdminGuard><DeskDetailPage /></CustomerAdminGuard>} />
                      <Route path="/desks/edit/:deskId" element={<CustomerAdminGuard><DeskEditPage /></CustomerAdminGuard>} />

                      {/* Doorbell routes */}
                      <Route path="/doorbells" element={<CustomerAdminGuard><DoorbellsPage /></CustomerAdminGuard>} />
                      <Route path="/doorbells/preset-messages" element={<CustomerAdminGuard><PresetMessagesPage /></CustomerAdminGuard>} />
                      <Route path="/doorbells/new" element={<CustomerAdminGuard><DoorbellUpsertPage /></CustomerAdminGuard>} />
                      <Route path="/doorbells/view/:id" element={<CustomerAdminGuard><DoorbellDetailPage /></CustomerAdminGuard>} />
                      <Route path="/doorbells/edit/:id" element={<CustomerAdminGuard><DoorbellUpsertPage /></CustomerAdminGuard>} />

                      {/* IoT routes — specific paths before :tab. Connections (broker connections,
                          provider rows, MQTT logins) is customer-admin: its writes are, and logins
                          carry credentials (navConfig ``customerAdminOnly``). */}
                      <Route path="/iot/devices" element={<IotDevicesPage />} />
                      <Route path="/iot/devices/view/:deviceId" element={<IotDeviceDetailPage />} />
                      <Route path="/iot/devices/:tab" element={<IotDevicesTabRedirect />} />
                      <Route path="/iot/providers/:providerId" element={<IotProviderPage />} />
                      <Route path="/iot/providers/:providerId/:tab" element={<IotProviderPage />} />
                      <Route path="/iot/mapping" element={<IotMappingPage />} />
                      <Route path="/iot/connections" element={<CustomerAdminGuard><IotConnectionsPage /></CustomerAdminGuard>} />
                      <Route path="/iot/connections/:tab" element={<CustomerAdminGuard><IotConnectionsPage /></CustomerAdminGuard>} />

                      {/* Acre Intrusion routes — detail before list */}
                      <Route path="/acre/connections/view/:id/:tab?" element={<SuperAdminGuard><AcreConnectionDetailPage /></SuperAdminGuard>} />
                      <Route path="/acre" element={<SuperAdminGuard><AcreDashboard /></SuperAdminGuard>} />

                      {/* FAQ routes — specific paths before :tab? catch-all */}
                      <Route path="/faq/categories/view/:categoryId" element={<CustomerAdminGuard><FAQCategoryDetailPage /></CustomerAdminGuard>} />
                      <Route path="/faq/articles/new" element={<CustomerAdminGuard><FAQArticleEditPage /></CustomerAdminGuard>} />
                      <Route path="/faq/articles/view/:articleId" element={<CustomerAdminGuard><FAQArticleViewPage /></CustomerAdminGuard>} />
                      <Route path="/faq/articles/edit/:articleId" element={<CustomerAdminGuard><FAQArticleEditPage /></CustomerAdminGuard>} />
                      <Route path="/faq/guides" element={<CustomerAdminGuard><GuidesPage /></CustomerAdminGuard>} />
                      <Route path="/faq/guides/new" element={<CustomerAdminGuard><GuideEditPage /></CustomerAdminGuard>} />
                      <Route path="/faq/guides/edit/:guideId" element={<CustomerAdminGuard><GuideEditPage /></CustomerAdminGuard>} />
                      <Route path="/faq/:tab?" element={<CustomerAdminGuard><FAQDashboard /></CustomerAdminGuard>} />

                      {/* Papercut routes — specific paths before :tab? catch-all */}
                      <Route path="/papercut/systems" element={<SuperAdminGuard><ManageSystemsPage /></SuperAdminGuard>} />
                      <Route path="/papercut/:tab?" element={<SuperAdminGuard><PapercutDashboard /></SuperAdminGuard>} />

                      {/* Mailroom — inbound email pipeline operator view */}
                      <Route path="/mailroom" element={<SuperAdminGuard><MailroomPage /></SuperAdminGuard>} />

                      {/* Support: tabbed Inbox + My Subscriptions (#173). Contact
                          group config now lives on the Site detail page
                          (/site#contact-groups) since groups are site-scoped.
                          Open to internal staff and customer admins, matching
                          the backend's require_access(customer_admin=True) on
                          every support/admin route and the nav leaf. */}
                      <Route path="/support" element={<CustomerAdminGuard><SupportPage /></CustomerAdminGuard>} />
                      <Route path="/support/tickets/view/:ticketId" element={<CustomerAdminGuard><SupportTicketDetailPage /></CustomerAdminGuard>} />

                      {/* Flow manager: a lifecycle event becomes one ticket per
                          target group. Open to internal staff and customer
                          admins, matching the backend's customer_admin guards
                          and the nav leaf. */}
                      <Route path="/flows" element={<CustomerAdminGuard><FlowsPage /></CustomerAdminGuard>} />
                      <Route path="/flows/view/:flowId" element={<CustomerAdminGuard><FlowDetailPage /></CustomerAdminGuard>} />
                      <Route path="/billing/readiness" element={<CustomerAdminGuard><BillingReadinessPage /></CustomerAdminGuard>} />
                      <Route path="/billing/profile" element={<CustomerAdminGuard><BillingProfilePage /></CustomerAdminGuard>} />
                      <Route path="/billing/connections" element={<CustomerAdminGuard><BillingConnectionsPage /></CustomerAdminGuard>} />
                      <Route path="/billing/runs" element={<CustomerAdminGuard><BillingRunsPage /></CustomerAdminGuard>} />
                      <Route path="/billing/runs/:runId" element={<CustomerAdminGuard><BillingRunDetailPage /></CustomerAdminGuard>} />
                      <Route path="/billing/invoices" element={<CustomerAdminGuard><BillingInvoicesPage /></CustomerAdminGuard>} />
                      <Route path="/billing/invoices/:invoiceId" element={<CustomerAdminGuard><BillingInvoiceDetailPage /></CustomerAdminGuard>} />
                      <Route path="/billing/adjustments/:adjustmentId" element={<CustomerAdminGuard><PriceAdjustmentRedirectPage /></CustomerAdminGuard>} />
                      <Route path="/billing/articles" element={<CustomerAdminGuard><BillingArticlesPage /></CustomerAdminGuard>} />
                      <Route path="/billing/charges/:chargeId" element={<CustomerAdminGuard><ExtraChargeViewPage /></CustomerAdminGuard>} />
                      <Route path="/needs-attention" element={<NeedsAttentionPage />} />

                      {/* Wisenet routes — specific paths before :tab? catch-all */}
                      <Route path="/wisenet/systems/view/:systemId" element={<SuperAdminGuard><WisenetSystemDetailPage /></SuperAdminGuard>} />
                      <Route path="/wisenet/systems" element={<SuperAdminGuard><WisenetManageSystemsPage /></SuperAdminGuard>} />
                      <Route path="/wisenet/:tab?" element={<SuperAdminGuard><WisenetDashboard /></SuperAdminGuard>} />

                      {/* Aruba Instant On routes — specific paths before the :tab? overview catch-all */}
                      <Route path="/arubainstanton/view/:systemId" element={<SuperAdminGuard><ArubaSystemDetailPage /></SuperAdminGuard>} />
                      <Route path="/arubainstanton/manage" element={<SuperAdminGuard><ArubaManagePage /></SuperAdminGuard>} />
                      <Route path="/arubainstanton/:tab?" element={<SuperAdminGuard><ArubaDashboard /></SuperAdminGuard>} />

                      {/* UniFi management dashboard — primary /unifi surface (mirrors /arubainstanton);
                          specific paths (manage, rf) before the :tab? catch-all — static segments
                          outrank the dynamic :tab? so /unifi/rf never resolves as a dashboard tab */}
                      <Route path="/unifi/manage" element={<SuperAdminGuard><UnifiManagePage /></SuperAdminGuard>} />
                      <Route path="/unifi/rf/scans/view/:scanId" element={<SuperAdminGuard><RFScanDetailPage /></SuperAdminGuard>} />
                      <Route path="/unifi/rf/:tab?" element={<SuperAdminGuard><UnifiRFDashboard /></SuperAdminGuard>} />
                      <Route path="/unifi/:tab?" element={<SuperAdminGuard><UnifiDashboard /></SuperAdminGuard>} />
                      {/* Legacy /unifi-rf bookmarks → /unifi/rf */}
                      <Route path="/unifi-rf/scans/view/:scanId" element={<Navigate to="/unifi/rf" replace />} />
                      <Route path="/unifi-rf/:tab?" element={<Navigate to="/unifi/rf" replace />} />

                      {/* Timeline routes */}
                      <Route path="/timeline" element={<TimelinePage />} />

                      {/* Audit log */}
                      <Route path="/audit-log" element={<CustomerAdminGuard><AuditLogPage /></CustomerAdminGuard>} />

                      {/* Changelog (release notes) */}
                      <Route path="/changelog" element={<ChangelogPage />} />

                      {/* Staff tooling (#459) — reached from the avatar menu, no nav leaf */}
                      <Route path="/system-settings" element={<StaffGuard><SystemSettingsPage /></StaffGuard>} />
                      <Route path="/demo-data" element={<StaffGuard><DemoDataPage /></StaffGuard>} />

                      {/* Locations overview */}
                      <Route path="/locations" element={<CustomerAdminGuard><LocationsOverviewPage /></CustomerAdminGuard>} />

                      {/* Floorplan routes */}
                      <Route path="/floorplans" element={<CustomerAdminGuard><FloorplansListPage /></CustomerAdminGuard>} />
                      <Route path="/floorplans/view/:id" element={<CustomerAdminGuard><FloorplanDetailPage /></CustomerAdminGuard>} />
                      <Route path="/floorplans/edit/:id" element={<CustomerAdminGuard><FloorplanEditorPage /></CustomerAdminGuard>} />
                      <Route path="/floorplans/points/:id" element={<CustomerAdminGuard><FloorplanPointsPage /></CustomerAdminGuard>} />
                      <Route path="/floorplans/navigation/:id" element={<CustomerAdminGuard><FloorplanNavigationPage /></CustomerAdminGuard>} />
                      <Route path="/floorplans/interior/:id" element={<CustomerAdminGuard><FloorplanInteriorPage /></CustomerAdminGuard>} />
                      <Route path="/floorplans/layers" element={<CustomerAdminGuard><FloorplanLayersPage /></CustomerAdminGuard>} />

                      {/* Mikrotik routes - only for superadmins */}
                      <Route path="/mikrotik/devices" element={<SuperAdminGuard><MikrotikDevicesPage /></SuperAdminGuard>} />
                      <Route path="/mikrotik/devices/view/:id" element={<SuperAdminGuard><MikrotikDeviceDetailPage /></SuperAdminGuard>} />
                      <Route path="/mikrotik/devices/new" element={<SuperAdminGuard><MikrotikDeviceCreatePage /></SuperAdminGuard>} />
                      <Route path="/mikrotik/devices/edit/:id" element={<SuperAdminGuard><MikrotikDeviceEditPage /></SuperAdminGuard>} />
                      <Route path="/mikrotik/devices/view/:id/config" element={<SuperAdminGuard><MikrotikDeviceConfigPage /></SuperAdminGuard>} />
                      <Route path="/mikrotik/devices/:deviceId/interfaces/:interfaceId" element={<SuperAdminGuard><MikrotikInterfaceDetailPage /></SuperAdminGuard>} />
                      <Route path="/mikrotik/dashboard/:tab?" element={<SuperAdminGuard><MikrotikDashboardPage /></SuperAdminGuard>} />

                      {/* Arista routes - only for superadmins */}
                      <Route path="/arista/devices" element={<SuperAdminGuard><AristaDevicesPage /></SuperAdminGuard>} />
                      <Route path="/arista/devices/view/:id" element={<SuperAdminGuard><AristaDeviceDetailPage /></SuperAdminGuard>} />
                      <Route path="/arista/devices/new" element={<SuperAdminGuard><AristaDeviceCreatePage /></SuperAdminGuard>} />
                      <Route path="/arista/devices/edit/:id" element={<SuperAdminGuard><AristaDeviceEditPage /></SuperAdminGuard>} />
                      <Route path="/arista/devices/view/:id/config" element={<SuperAdminGuard><AristaDeviceConfigPage /></SuperAdminGuard>} />
                      <Route path="/arista/devices/:deviceId/interfaces/:interfaceId" element={<SuperAdminGuard><AristaInterfaceDetailPage /></SuperAdminGuard>} />
                      <Route path="/arista/dashboard/:tab?" element={<SuperAdminGuard><AristaDashboardPage /></SuperAdminGuard>} />

                      {/* Sidecar routes - only for superadmins */}
                      <Route path="/sidecar" element={<SuperAdminGuard><SidecarPage /></SuperAdminGuard>} />

                      {/* Slack integration */}
                      <Route path="/slack" element={<CustomerAdminGuard><SlackBotsPage /></CustomerAdminGuard>} />

                      {/* Meeting-room booking + no-show dashboard (#18) */}
                      <Route path="/meeting-rooms/dashboard" element={<CustomerAdminGuard><MeetingRoomDashboardPage /></CustomerAdminGuard>} />

                      {/* Certificate routes - only for superadmins */}
                      <Route path="/certificates" element={<SuperAdminGuard><CertificatesPage /></SuperAdminGuard>} />
                      <Route path="/certificates/new" element={<SuperAdminGuard><CertificateCreatePage /></SuperAdminGuard>} />
                      <Route path="/certificates/view/:id" element={<SuperAdminGuard><CertificateDetailPage /></SuperAdminGuard>} />
                      <Route path="/certificates/edit/:id" element={<SuperAdminGuard><CertificateEditPage /></SuperAdminGuard>} />

                      {/* Diagnostic session detail (must be before /diagnostic) */}
                      <Route path="/diagnostic/session/:ref" element={<CustomerAdminGuard><DiagnosticSessionDetailPage /></CustomerAdminGuard>} />
                      {/* Diagnostic (replaces Connectivity tests + Speed test) */}
                      <Route path="/diagnostic/:tab?" element={<CustomerAdminGuard><DiagnosticSessionsPage /></CustomerAdminGuard>} />

                      {/* Network device routes */}
                      <Route path="/network-devices" element={<SuperAdminGuard><ClientsListPage /></SuperAdminGuard>} />
                      <Route path="/network-devices/:macAddress/:tab?" element={<SuperAdminGuard><ClientDetailPage /></SuperAdminGuard>} />

                      {/* Redirects */}
                      <Route path="/" element={<CustomerRedirect />} />
                    </Route>

                    {/* Catch-all route */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
                </MaintenanceGate>
                {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
            </SiteProvider>
          </AuthProvider>
      </ThemeProvider>
    </SentryErrorBoundary>
  );
};

export default App;
